/**
 * Field keys reach the document API in three shapes, and it accepts only one.
 *
 * The filter picker produces `GROUP.CODE`, straight from the schema endpoint.
 * The result table's columns are full `DOCUMENT.ATTRIBUTE.GROUP.CODE` dimension
 * paths. And a handful of columns are lifted to bare top-level names by
 * `measureToDocAiDocument`, carrying no group at all. Every request-facing
 * translation goes through here so the three shapes converge in one place
 * instead of each caller guessing.
 */

export interface SchemaField {
   group: string;
   code: string;
}

/**
 * `measureToDocAiDocument` lifts a few dimensions to top-level row keys so code
 * around the table can read them directly, which leaves those columns with a key
 * that has no group in it. Map each back to what the API sorts on: DOCID and
 * DOCTYPE are measure-level scalars, the rest are ordinary schema fields.
 */
const LIFTED_COLUMN_SORT_FIELDS: Record<string, string> = {
   docid: 'DOCID',
   doctype: 'DOCTYPE',
   bureau: 'CLASSIFICATION.BUREAU',
   filename: 'IDENTIFIER.FILE_NAME',
   docurl: 'ACCESS.DOCURL',
};

/**
 * Split a field key into its schema group and code.
 *
 * Accepts `GROUP.CODE` as the filter UI produces it, and a full
 * `DOCUMENT.ATTRIBUTE.GROUP.CODE` path as the result table's column keys carry —
 * a facet click hands us whichever the surface had. Returns null for anything
 * else, notably the raw NMED column names (`PermitNumber`) that filter state
 * saved before the schema migration still holds.
 */
export function splitSchemaFieldKey(key: string): SchemaField | null {
   const parts = key.split('.').filter(Boolean);
   if (parts.length < 2)
      return null;
   const [code, group] = [parts[parts.length - 1], parts[parts.length - 2]];
   if (!group || !code)
      return null;
   return { group, code };
}

/**
 * Translate a column key into the sort field the search API accepts.
 *
 * thresh raises on a field it cannot parse rather than falling back to relevancy,
 * so an untranslated dimension path would fail the whole search instead of merely
 * ordering it wrong — `DOCUMENT.ATTRIBUTE.DATE.SIGNED` partitions to a group of
 * `DOCUMENT`, which is not a MetadataGroup.
 *
 * Returns null when the key resolves to nothing sortable; callers send that as
 * "no sort", which is relevancy order.
 */
export function toSortField(key: string): string | null {
   if (!key)
      return null;

   const lifted = LIFTED_COLUMN_SORT_FIELDS[key.toLowerCase()];
   if (lifted)
      return lifted;

   const field = splitSchemaFieldKey(key);
   return field ? `${field.group}.${field.code}` : null;
}

/**
 * Whether a filter key names a field the given schema knows about.
 *
 * `schemaFieldKeys` are `GROUP.CODE`; the key may be either that or a full path.
 * An empty schema answers true — the schema request hasn't landed yet, and
 * flagging every filter as unknown in that window would be worse than waiting.
 */
export function isKnownSchemaField(key: string, schemaFieldKeys: string[]): boolean {
   if (schemaFieldKeys.length === 0)
      return true;
   const field = splitSchemaFieldKey(key);
   return field !== null && schemaFieldKeys.includes(`${field.group}.${field.code}`);
}
